// Zweck: HTTP und dauerhafte Warteschlange für den isolierten Docker-Testhost. Stand: 21.10.2026.
import localforage from 'localforage';
import type {
  DeviceTransport,
  DeviceSearchFilter,
  SimLocal,
  SimOutcome,
  SimOperation,
  SimState,
  SimEvent,
  SimDevice,
  SimCapture,
  SimQueueEntry,
} from '../interfaces/deviceSimulation';
export const simStore = localforage.createInstance({
  name: 'warehouse-device-simulation',
  storeName: 'local',
});
export const emptySim = (): SimLocal => ({
  snapshot: null,
  draft: null,
  queue: [],
});
export const availableDevice = (d: SimDevice) =>
  d.state === 'in_stock' && d.condition === 'ok' && !!d.serial;
// Auch mehrere abgelehnte Berichtigungsversuche gehören zur selben Meldung.
// Erst eine bestätigte Berichtigung derselben Art erledigt ihre Vorgänger.
export function resolvedSimulationRequests(local: SimLocal): Set<string> {
  const entries = [
    ...(local.snapshot?.bookings ?? []).map((b) => ({
      operation: b.booking,
      state: b.status,
    })),
    ...local.queue,
  ];
  const byId = new Map(entries.map((q) => [q.operation.requestId, q]));
  const resolved = new Set<string>();
  for (const q of entries.filter((q) => q.state === 'confirmed')) {
    let id = q.operation.replacesId;
    const seen = new Set<string>();
    while (id && !seen.has(id)) {
      seen.add(id);
      const previous = byId.get(id);
      if (!previous || previous.operation.kind !== q.operation.kind) break;
      resolved.add(id);
      id = previous.operation.replacesId;
    }
  }
  return resolved;
}
export const captureStarted = (c?: SimCapture | null) =>
  !!c &&
  (c.kind === 'damage' ||
    !!c.serial ||
    !!c.note ||
    c.missing ||
    c.physical ||
    c.condition !== 'ok');
export const canStopSimulation = (q: SimQueueEntry) =>
  q.state === 'pending' && q.attempted === false;
export function stopSimulation(local: SimLocal, id: string): SimLocal {
  const q = local.queue.find((x) => x.operation.requestId === id);
  if (!q || !canStopSimulation(q))
    throw new Error('Übertragung bereits gestartet. Ergebnis prüfen.');
  return {
    ...local,
    queue: local.queue.map((x) => (x === q ? { ...x, state: 'stopped' } : x)),
  };
}
export const simMatches = (text: string, query: string) => {
  const normalize = (s: string) =>
    s
      .toLocaleLowerCase('de')
      .replace(/ae/g, 'ä')
      .replace(/oe/g, 'ö')
      .replace(/ue/g, 'ü')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  return normalize(query)
    .trim()
    .split(/\s+/)
    .every((word) => normalize(text).includes(word));
};
export function simulationStock(
  local: SimLocal,
  articleId: string,
  locationId: string,
) {
  const stock = local.snapshot?.stocks.find(
    (s) => s.articleId === articleId && s.locationId === locationId,
  );
  if (!stock) return null;
  return (
    stock.quantity -
    local.queue
      .filter(
        (q) =>
          (['pending', 'unknown'].includes(q.state) ||
            (q.state === 'confirmed' && q.stockPendingRefresh === true)) &&
          q.operation.kind === 'issue' &&
          q.operation.locationId === locationId,
      )
      .reduce(
        (total, q) =>
          total +
          (q.operation.lines.find((l) => l.articleId === articleId)?.quantity ??
            0),
        0,
      )
  );
}
// Nur schon vor dem Abruf bestätigte Vorgänge sind sicher im neuen Bestand.
// Das gilt auch, wenn sie aus den letzten 200 Lagerbucheinträgen gefallen sind.
export function withDeviceSnapshot(
  local: SimLocal,
  snapshot: SimState,
  confirmedBeforeRead: Set<string>,
): SimLocal {
  const results = new Map(snapshot.bookings.map((b) => [b.requestId, b]));
  return {
    ...local,
    snapshot,
    accessDenied: undefined,
    snapshotAt: new Date().toISOString(),
    queue: local.queue.map((q) => {
      const found = results.get(q.operation.requestId);
      const updated =
        found && ['pending', 'unknown'].includes(q.state)
          ? { ...q, state: found.status, result: found }
          : q;
      return found || confirmedBeforeRead.has(q.operation.requestId)
        ? { ...updated, stockPendingRefresh: false }
        : updated;
    }),
  };
}
export function simulationApi(user: string, workspace: string) {
  async function call<T>(
    path: string,
    body?: unknown,
    lose = false,
  ): Promise<T> {
    const response = await fetch('/simulation/api/' + path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Test-User': user,
        'X-Test-Workspace': workspace,
        ...(lose ? { 'X-Test-Lose-Response': '1' } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: 'no-store',
      signal: AbortSignal.timeout(15000),
    }).catch(() => {
      throw new Error(
        'Keine Bestätigung erhalten. Verbindung herstellen und Übertragung erneut prüfen.',
      );
    });
    if (response.status === 404) return null as T;
    if (!response.ok) {
      const text = await response
        .json()
        .catch(() => ({ message: 'Server nicht erreichbar.' }));
      throw Object.assign(
        new Error(text.message ?? 'Anfrage abgelehnt. Angaben prüfen.'),
        { status: response.status },
      );
    }
    return response.json();
  }
  return {
    state: () => call<SimState>('state'),
    status: (id: string) => call<SimOutcome | null>('bookings/' + id),
    book: (op: SimOperation, lose = false) =>
      call<SimOutcome>('bookings', op, lose),
    history: (id: string) => call<SimEvent[]>('devices/' + id + '/history'),
    search: (s: string, filter?: DeviceSearchFilter) =>
      call<SimDevice[]>(
        'devices?search=' +
          encodeURIComponent(s) +
          Object.entries(filter ?? {})
            .map(([key, value]) => `&${key}=${encodeURIComponent(value)}`)
            .join(''),
      ),
    hero: (mode: string) => call('hero?mode=' + mode, {}),
  };
}
export async function sendSimulationQueue(
  local: SimLocal,
  api: DeviceTransport,
  save: (x: SimLocal) => Promise<void>,
  maySend: () => boolean,
  lose = false,
) {
  const next = structuredClone(local);
  for (const q of next.queue) {
    if (!maySend()) break;
    if (!['pending', 'unknown'].includes(q.state)) continue;
    let result: SimOutcome | null = null;
    if (q.state === 'unknown' || q.attempted !== false)
      result = await api.status(q.operation.requestId);
    if (!maySend()) break;
    q.state = 'unknown';
    q.attempted = true;
    await save(next);
    if (!maySend()) break;
    result ??= await api.book(q.operation, lose);
    lose = false;
    q.result = result;
    q.state = result.status;
    q.stockPendingRefresh = result.status === 'confirmed';
    await save(next);
  }
  return next;
}
